import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  SkipNext as SkipNextIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon,
  Lock as LockIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import vendedorService, { Vendedor } from '../../services/vendedorService';
import VendedorSelect from '../../components/VendedorSelect';
import visitasPlanService from '../../services/visitasPlanService';
import { enrichPlanesWithMoneyMetrics } from '../../services/visitasPlanMetricsService';
import {
  loadMontosPorVisitaEnPlan,
  VisitaOperacionMontos,
} from '../../services/visitasPlanOperacionesService';
import visitasService from '../../services/visitasService';
import { formatCurrency } from '../../utils/formatters';
import {
  CreateVisitasDto,
  CreateVisitasPlanRangePayload,
  CreateVisitasRapidaDto,
  PagedResult,
  UpdateVisitasEstadoDto,
  UpdateVisitasPlanDto,
  VisitasDto,
  VisitasHistorialDto,
  VisitasPlanIndicadoresDto,
  VisitasPlanDto,
} from '../../types/visitas';
import PlanFormDialog from './components/PlanFormDialog';
import VisitasAgendaTab from './components/VisitasAgendaTab';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  buildCreatePlanItemsPayload,
  eachIsoDayInRange,
  shouldIncludeDraftOnDay,
} from './components/planFormHelpers';
import AppendVisitaDialog from './components/AppendVisitaDialog';
import AgregarClientesPlanDialog from './components/AgregarClientesPlanDialog';
import VisitaRapidaDialog from './components/VisitaRapidaDialog';
import VisitaEstadoDialog from './components/VisitaEstadoDialog';
import VisitaHistorialDialog from './components/VisitaHistorialDialog';
import {
  PlanIndicadoresTableCells,
  PlanIndicadoresMobileSummary,
} from './components/PlanIndicadoresTableCells';
import VisitasPlanSearchDialog from './components/VisitasPlanSearchDialog';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import { exportVisitasPlanPdf } from './exportVisitasPlanPdf';

type TabValue = 'planes' | 'visitas' | 'agenda';

interface VendedorOption {
  nroVendedor: number;
  nombreVendedor?: string;
  usuario?: string;
}

interface PlanOption {
  idVisitaPlan: number;
  fecha: string;
  nroVendedor: number;
  nombreVendedor?: string | null;
}

type PlanActionTarget = {
  idVisitaPlan: number;
  fecha: string;
  nroVendedor: number;
  nombreVendedor?: string | null;
  usuarioPlanificador?: string | null;
  fechaCreacion: string;
  estadoPlan: string;
  observaciones?: string | null;
  origenCreacion?: string | null;
};

const emptyPaged = <T,>(): PagedResult<T> => ({
  items: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 10,
  totalPages: 0,
});

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-AR');
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('es-AR');
};

const renderClientePlanDetalle = (v: VisitasDto) => (
  <Typography variant="body2">
    {v.nombreCliente || String(v.nroCliente)}
  </Typography>
);

const renderDireccionPlanDetalle = (v: VisitasDto) => (
  <Typography variant="body2">
    {v.descripcionDireccion?.trim() || v.codigoDireccion?.trim() || '-'}
  </Typography>
);

const formatMontoVisitaOperacion = (monto: number | null | undefined): string => {
  if (monto == null || !Number.isFinite(monto) || monto <= 0) return '-';
  return formatCurrency(monto);
};

const formatVendedor = (nroVendedor: number, nombreVendedor?: string | null): string =>
  nombreVendedor ? `${nroVendedor} - ${nombreVendedor}` : String(nroVendedor);

const resolveVendedorUsuario = (
  nroVendedor: number,
  vendedores: VendedorOption[],
): string => {
  const v = vendedores.find((x) => x.nroVendedor === nroVendedor);
  return v?.usuario?.trim() || v?.nombreVendedor?.trim() || '-';
};

const toPlanDto = (plan: PlanActionTarget): VisitasPlanDto => ({
  idVisitaPlan: plan.idVisitaPlan,
  fecha: plan.fecha,
  nroVendedor: plan.nroVendedor,
  usuarioPlanificador: plan.usuarioPlanificador,
  fechaCreacion: plan.fechaCreacion,
  estadoPlan: plan.estadoPlan,
  observaciones: plan.observaciones,
  origenCreacion: plan.origenCreacion,
  visitas: null,
});

const normalizeEstado = (estado?: string | null): string =>
  String(estado || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[\s_-]+/g, '');

/** El API a veces varia casing/acentos; los botones deben tolerarlo. */
const isVisitaPendiente = (visita: VisitasDto): boolean => {
  const e = normalizeEstado(visita.estado);
  return e === 'pendiente';
};

const isVisitaIniciada = (visita: VisitasDto): boolean => {
  const e = normalizeEstado(visita.estado);
  return e === 'iniciada' || e === 'iniciado' || e === 'encurso';
};

const canCompletarVisita = (visita: VisitasDto): boolean => isVisitaIniciada(visita);

const canOmitirVisita = (visita: VisitasDto): boolean =>
  isVisitaPendiente(visita) || isVisitaIniciada(visita);

/** Visitas sin orden (0 o vacío) van al final, igual que en eOne Mobile. */
const ordenSortKey = (orden?: number | null): number =>
  Number(orden) > 0 ? Number(orden) : Number.MAX_SAFE_INTEGER;

const sortVisitasByOrden = (rows: VisitasDto[]): VisitasDto[] =>
  [...rows].sort(
    (a, b) => (ordenSortKey(a.orden) - ordenSortKey(b.orden)) || (a.idVisita - b.idVisita),
  );

const pendingOrderSignature = (rows: VisitasDto[]): string =>
  sortVisitasByOrden(rows)
    .filter(isVisitaPendiente)
    .map((v) => v.idVisita)
    .join(',');

const reorderOnlyPendientes = (rows: VisitasDto[], pendingIdsInNewOrder: number[]): VisitasDto[] => {
  const sorted = sortVisitasByOrden(rows);
  const pendientes = sorted.filter(isVisitaPendiente);
  const pendingSlots = pendientes.map((v) => v.orden).sort((a, b) => ordenSortKey(a) - ordenSortKey(b));
  const pendingById = new Map(pendientes.map((v) => [v.idVisita, v]));

  const updatedPending = new Map<number, VisitasDto>();
  pendingIdsInNewOrder.forEach((idVisita, idx) => {
    const row = pendingById.get(idVisita);
    if (!row) return;
    updatedPending.set(idVisita, {
      ...row,
      orden: pendingSlots[idx],
    });
  });

  return sortVisitasByOrden(sorted.map((row) => updatedPending.get(row.idVisita) || row));
};

interface SortablePlanVisitaRowProps {
  visita: VisitasDto;
  operaciones?: VisitaOperacionMontos;
  onRemovePendiente?: (visita: VisitasDto) => void;
  removeDisabled?: boolean;
}

const SortablePlanVisitaRow: React.FC<SortablePlanVisitaRowProps> = ({
  visita,
  operaciones,
  onRemovePendiente,
  removeDisabled = true,
}) => {
  const draggable = isVisitaPendiente(visita);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(visita.idVisita),
    disabled: !draggable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    backgroundColor: isDragging ? '#f7fbff' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {draggable ? (
            <IconButton
              size="small"
              color="primary"
              title="Arrastrar para reordenar (solo pendientes)"
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          ) : (
            <LockIcon fontSize="small" color="disabled" titleAccess="Orden bloqueado por estado" />
          )}
          <Typography variant="body2">{visita.orden > 0 ? visita.orden : '–'}</Typography>
        </Stack>
      </TableCell>
      <TableCell>{renderClientePlanDetalle(visita)}</TableCell>
      <TableCell>{renderDireccionPlanDetalle(visita)}</TableCell>
      <TableCell>
        <Typography variant="body2">{visita.observaciones?.trim() || '-'}</Typography>
      </TableCell>
      <TableCell align="right">
        {formatMontoVisitaOperacion(operaciones?.cobranzaMonto)}
      </TableCell>
      <TableCell align="right">
        {formatMontoVisitaOperacion(operaciones?.pedidoMonto)}
      </TableCell>
      <TableCell>{visita.estado}</TableCell>
      <TableCell>{formatDateTime(visita.fechaHoraProgramada)}</TableCell>
      <TableCell align="center">
        <Tooltip
          title={
            removeDisabled
              ? 'No se puede quitar (plan cerrado o visita no pendiente)'
              : 'Quitar visita del plan'
          }
        >
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={removeDisabled || !draggable}
              onClick={() => onRemovePendiente?.(visita)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const Visitas: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const initialTab: TabValue =
    tabParam === 'visitas' ? 'visitas' : tabParam === 'agenda' ? 'agenda' : 'planes';
  const [tab, setTab] = useState<TabValue>(initialTab);

  const [vendedores, setVendedores] = useState<VendedorOption[]>([]);

  const usuarioPorVendedor = useMemo(() => {
    const map = new Map<number, string>();
    vendedores.forEach((v) => {
      const usuario = v.usuario?.trim();
      if (usuario) map.set(v.nroVendedor, usuario);
    });
    return map;
  }, [vendedores]);

  const [planesData, setPlanesData] = useState<PagedResult<VisitasPlanIndicadoresDto>>(emptyPaged());
  const [planesLoading, setPlanesLoading] = useState(false);
  const planesRequestIdRef = useRef(0);
  const [planesPage, setPlanesPage] = useState(0);
  const [planesRowsPerPage, setPlanesRowsPerPage] = useState(10);

  const [visitasData, setVisitasData] = useState<PagedResult<VisitasDto>>(emptyPaged());
  const [visitasLoading, setVisitasLoading] = useState(false);
  const [visitasPage, setVisitasPage] = useState(0);
  const [visitasRowsPerPage, setVisitasRowsPerPage] = useState(10);
  const [visitasPlanOptions, setVisitasPlanOptions] = useState<PlanOption[]>([]);
  const [visitasPlanOptionsLoading, setVisitasPlanOptionsLoading] = useState(false);

  const [planesFilters, setPlanesFilters] = useState({
    fechaDesde: '',
    fechaHasta: '',
    nroVendedor: '',
    estadoPlan: 'EnCurso',
  });
  const [visitasFilters, setVisitasFilters] = useState({
    idVisitaPlan: '',
    nroVendedor: '',
    fechaDesde: '',
    fechaHasta: '',
    estado: '',
    nroCliente: '',
  });

  const [planCreateOpen, setPlanCreateOpen] = useState(false);
  const [planEditOpen, setPlanEditOpen] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<VisitasPlanDto | null>(null);

  const [planDetailOpen, setPlanDetailOpen] = useState(false);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [planDetail, setPlanDetail] = useState<VisitasPlanDto | null>(null);
  const [planDetailVisitasDraft, setPlanDetailVisitasDraft] = useState<VisitasDto[]>([]);
  const [planDetailOperaciones, setPlanDetailOperaciones] = useState<
    Record<number, VisitaOperacionMontos>
  >({});
  const [planOrderDirty, setPlanOrderDirty] = useState(false);
  const [planOrderSaving, setPlanOrderSaving] = useState(false);
  const [planDetailMutating, setPlanDetailMutating] = useState(false);

  const [appendOpen, setAppendOpen] = useState(false);
  const [appendSaving, setAppendSaving] = useState(false);
  const [appendPlan, setAppendPlan] = useState<PlanActionTarget | null>(null);

  const [agregarClientesPlanId, setAgregarClientesPlanId] = useState<number | null>(null);
  const [agregarClientesExistentes, setAgregarClientesExistentes] = useState<VisitasDto[]>([]);
  const [agregarClientesSaving, setAgregarClientesSaving] = useState(false);

  const [planPdfLoadingId, setPlanPdfLoadingId] = useState<number | null>(null);
  const [planActionsAnchor, setPlanActionsAnchor] = useState<{
    el: HTMLElement;
    plan: PlanActionTarget;
  } | null>(null);

  const [visitaRapidaOpen, setVisitaRapidaOpen] = useState(false);
  const [visitaRapidaSaving, setVisitaRapidaSaving] = useState(false);

  const [estadoOpen, setEstadoOpen] = useState(false);
  const [estadoSaving, setEstadoSaving] = useState(false);
  const [estadoAction, setEstadoAction] = useState<'iniciar' | 'completar' | 'omitir'>('iniciar');
  const [selectedVisita, setSelectedVisita] = useState<VisitasDto | null>(null);

  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialItems, setHistorialItems] = useState<VisitasHistorialDto[]>([]);
  const [historialVisitaId, setHistorialVisitaId] = useState<number | null>(null);

  const [clienteFiltroSearchOpen, setClienteFiltroSearchOpen] = useState(false);
  const [visitaFiltroClienteLabel, setVisitaFiltroClienteLabel] = useState('');
  const [planFiltroSearchOpen, setPlanFiltroSearchOpen] = useState(false);
  const [visitaFiltroPlanLabel, setVisitaFiltroPlanLabel] = useState('');

  const planDetailSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    const nextTab: TabValue =
      tabParam === 'visitas' ? 'visitas' : tabParam === 'agenda' ? 'agenda' : 'planes';
    setTab(nextTab);
  }, [tabParam]);

  const loadVendedores = useCallback(async () => {
    try {
      const data = await vendedorService.getVendedoresPaged({
        pageNumber: 1,
        pageSize: 200,
      });
      setVendedores(
        (data.items || []).map((v: Vendedor) => ({
          nroVendedor: v.nroVendedor,
          nombreVendedor: v.nombreVendedor,
          usuario: v.usuario,
        })),
      );
    } catch {
      setVendedores([]);
    }
  }, []);

  const fetchPlanes = useCallback(async () => {
    const requestId = ++planesRequestIdRef.current;
    const isCurrent = () => requestId === planesRequestIdRef.current;
    let data: PagedResult<VisitasPlanIndicadoresDto>;
    try {
      setPlanesLoading(true);
      data = await visitasPlanService.getPlanesIndicadores({
        fechaDesde: planesFilters.fechaDesde || undefined,
        fechaHasta: planesFilters.fechaHasta || undefined,
        nroVendedor: planesFilters.nroVendedor ? Number(planesFilters.nroVendedor) : undefined,
        estadoPlan: planesFilters.estadoPlan || undefined,
        pageNumber: planesPage + 1,
        pageSize: planesRowsPerPage,
      });
      if (!isCurrent()) return;
      setPlanesData(data);
    } catch (error: any) {
      if (!isCurrent()) return;
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar planes de visitas', {
        variant: 'error',
      });
      setPlanesData(emptyPaged());
      return;
    } finally {
      if (isCurrent()) setPlanesLoading(false);
    }

    // Los montos recorren pedidos, recibos y remitos de cada plan: se completan sin bloquear
    // la tabla ni a quien espera fetchPlanes.
    void enrichPlanesWithMoneyMetrics(data.items || [], usuarioPorVendedor)
      .then((itemsWithMetrics) => {
        if (isCurrent()) setPlanesData({ ...data, items: itemsWithMetrics });
      })
      .catch(() => {
        /* se conservan los indicadores que devolvió la API */
      });
  }, [enqueueSnackbar, planesFilters, planesPage, planesRowsPerPage, usuarioPorVendedor]);

  const fetchVisitas = useCallback(async () => {
    try {
      setVisitasLoading(true);
      const data = await visitasService.getVisitas({
        idVisitaPlan: visitasFilters.idVisitaPlan ? Number(visitasFilters.idVisitaPlan) : undefined,
        nroVendedor: visitasFilters.nroVendedor ? Number(visitasFilters.nroVendedor) : undefined,
        fechaDesde: visitasFilters.fechaDesde || undefined,
        fechaHasta: visitasFilters.fechaHasta || undefined,
        estado: visitasFilters.estado || undefined,
        nroCliente: visitasFilters.nroCliente ? Number(visitasFilters.nroCliente) : undefined,
        pageNumber: visitasPage + 1,
        pageSize: visitasRowsPerPage,
      });
      setVisitasData(data);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar visitas', {
        variant: 'error',
      });
      setVisitasData(emptyPaged());
    } finally {
      setVisitasLoading(false);
    }
  }, [enqueueSnackbar, visitasFilters, visitasPage, visitasRowsPerPage]);

  const fetchVisitasPlanOptions = useCallback(async () => {
    try {
      setVisitasPlanOptionsLoading(true);
      const data = await visitasPlanService.getPlanesIndicadores({
        fechaDesde: visitasFilters.fechaDesde || undefined,
        fechaHasta: visitasFilters.fechaHasta || undefined,
        nroVendedor: visitasFilters.nroVendedor ? Number(visitasFilters.nroVendedor) : undefined,
        pageNumber: 1,
        pageSize: 200,
      });
      setVisitasPlanOptions(
        (data.items || []).map((p) => ({
          idVisitaPlan: p.idVisitaPlan,
          fecha: p.fecha,
          nroVendedor: p.nroVendedor,
          nombreVendedor: p.nombreVendedor,
        })),
      );
    } catch {
      setVisitasPlanOptions([]);
    } finally {
      setVisitasPlanOptionsLoading(false);
    }
  }, [visitasFilters.fechaDesde, visitasFilters.fechaHasta, visitasFilters.nroVendedor]);

  useEffect(() => {
    loadVendedores();
  }, [loadVendedores]);

  useEffect(() => {
    if (tab === 'planes') fetchPlanes();
  }, [tab, fetchPlanes]);

  useEffect(() => {
    if (tab === 'visitas') fetchVisitas();
  }, [tab, fetchVisitas]);

  useEffect(() => {
    if (tab === 'visitas') fetchVisitasPlanOptions();
  }, [tab, fetchVisitasPlanOptions]);

  const handleChangeTab = (_: React.SyntheticEvent, value: TabValue) => {
    setSearchParams({ tab: value });
  };

  const refreshAll = async () => {
    await Promise.all([fetchPlanes(), fetchVisitas()]);
  };

  const handleCreatePlan = async (payload: CreateVisitasPlanRangePayload) => {
    const days = eachIsoDayInRange(payload.fechaDesde, payload.fechaHasta);
    if (days.length === 0) {
      enqueueSnackbar('El rango de fechas no es válido.', { variant: 'error' });
      return;
    }

    const porDia =
      payload.visitasPorDia && payload.visitasPorDia.length > 0
        ? payload.visitasPorDia
        : days
            .map((day) => ({
              fecha: day,
              visitDrafts: (payload.visitDrafts ?? []).filter((draft) =>
                shouldIncludeDraftOnDay(draft, day),
              ),
            }))
            .filter((day) => day.visitDrafts.length > 0);

    const totalDrafts = porDia.reduce((sum, day) => sum + day.visitDrafts.length, 0);

    try {
      setPlanSaving(true);

      let planesCreados = 0;
      let visitasAgregadas = 0;
      const skippedDays: string[] = [];
      let lastError: string | null = null;

      for (const { fecha: day, visitDrafts: dayDrafts } of porDia) {
        const visitDraftRows = dayDrafts.map((draft) => ({
          item: {
            nroCliente: draft.nroCliente,
            codigoDireccion: draft.codigoDireccion,
            fechaHoraProgramada: null,
            observaciones: draft.observaciones,
          },
          horarioPreferido: draft.horarioPreferido,
        }));
        const items = buildCreatePlanItemsPayload(day, visitDraftRows);
        if (items.length === 0) continue;
        try {
          const planes = await visitasPlanService.getPlanes({
            fechaDesde: day,
            fechaHasta: day,
            nroVendedor: payload.nroVendedor,
            pageNumber: 1,
            pageSize: 5,
          });
          const existing = planes.items[0];
          if (existing) {
            const full = await visitasPlanService.getPlan(existing.idVisitaPlan);
            for (const item of items) {
              const cod = (item.codigoDireccion ?? '').trim();
              const duplicada = (full.visitas ?? []).some(
                (v) =>
                  v.nroCliente === item.nroCliente &&
                  String(v.codigoDireccion ?? '').trim() === cod,
              );
              if (duplicada) continue;
              await visitasPlanService.addVisitaToPlan(existing.idVisitaPlan, {
                nroCliente: item.nroCliente,
                codigoDireccion: item.codigoDireccion,
                fechaHoraProgramada: item.fechaHoraProgramada,
                observaciones: item.observaciones,
              });
              visitasAgregadas += 1;
            }
          } else {
            await visitasPlanService.createPlan({
              fecha: day,
              nroVendedor: payload.nroVendedor,
              observaciones: payload.observaciones,
              items,
            });
            planesCreados += 1;
            visitasAgregadas += items.length;
          }
        } catch (error: any) {
          const status = error?.response?.status;
          const message = error?.response?.data?.message || 'Error al crear el plan';
          if (status === 409) {
            skippedDays.push(day);
            continue;
          }
          lastError = message;
          break;
        }
      }

      if (visitasAgregadas === 0 && skippedDays.length === 0 && totalDrafts > 0) {
        enqueueSnackbar(lastError || 'Error al agregar visitas al plan', { variant: 'error' });
        return;
      }

      if (planesCreados === 0 && visitasAgregadas === 0 && skippedDays.length === porDia.length) {
        enqueueSnackbar('No se agregaron visitas (planes existentes o sin filas válidas).', {
          variant: 'warning',
        });
        return;
      }

      if (skippedDays.length > 0 || lastError) {
        const omitidos = skippedDays.length > 0 ? ` Días omitidos: ${skippedDays.length}.` : '';
        const parcial = lastError ? ` Error: ${lastError}` : '';
        enqueueSnackbar(
          `Visitas agregadas: ${visitasAgregadas}. Planes nuevos: ${planesCreados}.${omitidos}${parcial}`,
          { variant: 'warning' },
        );
      } else if (visitasAgregadas > 0) {
        enqueueSnackbar(
          visitasAgregadas === 1
            ? '1 visita agregada al plan'
            : `${visitasAgregadas} visitas agregadas (${planesCreados} plan(es) nuevo(s))`,
          { variant: 'success' },
        );
      } else {
        enqueueSnackbar('No hay visitas para agregar.', { variant: 'info' });
      }

      setPlanCreateOpen(false);
      await fetchPlanes();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al crear el plan', {
        variant: 'error',
      });
    } finally {
      setPlanSaving(false);
    }
  };

  const handleUpdatePlan = async (payload: UpdateVisitasPlanDto) => {
    if (!selectedPlan) return;
    try {
      setPlanSaving(true);
      await visitasPlanService.updatePlan(selectedPlan.idVisitaPlan, payload);
      enqueueSnackbar('Plan actualizado correctamente', { variant: 'success' });
      setPlanEditOpen(false);
      setSelectedPlan(null);
      await fetchPlanes();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al actualizar el plan', {
        variant: 'error',
      });
    } finally {
      setPlanSaving(false);
    }
  };

  const handleDeletePlan = async (plan: PlanActionTarget) => {
    if (!window.confirm(`Se eliminará el plan #${plan.idVisitaPlan}. ¿Desea continuar?`)) return;
    try {
      await visitasPlanService.deletePlan(plan.idVisitaPlan);
      enqueueSnackbar('Plan eliminado correctamente', { variant: 'success' });
      await fetchPlanes();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar plan', {
        variant: 'error',
      });
    }
  };

  const handlePlanActionsOpen = (event: React.MouseEvent<HTMLElement>, plan: PlanActionTarget) => {
    setPlanActionsAnchor({ el: event.currentTarget, plan });
  };

  const handlePlanActionsClose = () => {
    setPlanActionsAnchor(null);
  };

  const renderPlanActions = (plan: PlanActionTarget) => (
    <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
      <Tooltip title="Agregar visita">
        <IconButton
          size="small"
          color="success"
          onClick={() => {
            setAppendPlan(plan);
            setAppendOpen(true);
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ver detalle">
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            void handleOpenPlanDetail(plan);
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Más acciones">
        <IconButton size="small" onClick={(e) => handlePlanActionsOpen(e, plan)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const handleImprimirPlanPdf = async (plan: PlanActionTarget) => {
    if (planPdfLoadingId === plan.idVisitaPlan) return;
    try {
      setPlanPdfLoadingId(plan.idVisitaPlan);
      const full = await visitasPlanService.getPlan(plan.idVisitaPlan);
      await exportVisitasPlanPdf(full, {
        vendedorLabel: formatVendedor(plan.nroVendedor, plan.nombreVendedor),
      });
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al generar PDF del plan', {
        variant: 'error',
      });
    } finally {
      setPlanPdfLoadingId(null);
    }
  };

  const handleOpenPlanDetail = async (plan: PlanActionTarget) => {
    try {
      setPlanDetailOpen(true);
      setPlanDetailLoading(true);
      setPlanDetailOperaciones({});
      const full = await visitasPlanService.getPlan(plan.idVisitaPlan);
      const visitasOrdenadas = sortVisitasByOrden(full.visitas || []);
      setPlanDetail(full);
      setPlanDetailVisitasDraft(visitasOrdenadas);
      setPlanOrderDirty(false);

      const operaciones = await loadMontosPorVisitaEnPlan(
        full.fecha,
        full.nroVendedor,
        usuarioPorVendedor.get(full.nroVendedor),
        visitasOrdenadas.map((v) => ({
          idVisita: v.idVisita,
          nroCliente: v.nroCliente,
          orden: v.orden,
          fechaHoraInicio: v.fechaHoraInicio,
          fechaHoraFin: v.fechaHoraFin,
          fechaHoraOmitida: v.fechaHoraOmitida,
        })),
      );
      setPlanDetailOperaciones(operaciones);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al obtener detalle del plan', {
        variant: 'error',
      });
      setPlanDetailOpen(false);
      setPlanDetailOperaciones({});
    } finally {
      setPlanDetailLoading(false);
    }
  };

  const handlePlanDetailDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = Number(active.id);
    const overId = Number(over.id);
    if (!Number.isFinite(activeId) || !Number.isFinite(overId)) return;

    const pendingIds = sortVisitasByOrden(planDetailVisitasDraft)
      .filter(isVisitaPendiente)
      .map((v) => v.idVisita);

    const oldIndex = pendingIds.indexOf(activeId);
    const newIndex = pendingIds.indexOf(overId);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedPendingIds = arrayMove(pendingIds, oldIndex, newIndex);
    const nextRows = reorderOnlyPendientes(planDetailVisitasDraft, reorderedPendingIds);
    setPlanDetailVisitasDraft(nextRows);
    setPlanOrderDirty(pendingOrderSignature(nextRows) !== pendingOrderSignature(planDetail?.visitas || []));
  };

  const handleSavePlanOrder = async () => {
    if (!planDetail || !planOrderDirty) return;

    const payload = {
      items: sortVisitasByOrden(planDetailVisitasDraft)
        .filter(isVisitaPendiente)
        .map((v) => ({ idVisita: v.idVisita })),
    };

    try {
      setPlanOrderSaving(true);
      await visitasPlanService.reordenarPendientes(planDetail.idVisitaPlan, payload);
      enqueueSnackbar('Orden de visitas actualizado correctamente', { variant: 'success' });
      const full = await visitasPlanService.getPlan(planDetail.idVisitaPlan);
      setPlanDetail(full);
      setPlanDetailVisitasDraft(sortVisitasByOrden(full.visitas || []));
      setPlanOrderDirty(false);
      await Promise.all([fetchPlanes(), fetchVisitas()]);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el nuevo orden de visitas', {
        variant: 'error',
      });
      if (planDetail?.idVisitaPlan) {
        const full = await visitasPlanService.getPlan(planDetail.idVisitaPlan);
        setPlanDetail(full);
        setPlanDetailVisitasDraft(sortVisitasByOrden(full.visitas || []));
        setPlanOrderDirty(false);
      }
    } finally {
      setPlanOrderSaving(false);
    }
  };

  const reloadPlanDetail = async (idVisitaPlan: number) => {
    const full = await visitasPlanService.getPlan(idVisitaPlan);
    const visitasOrdenadas = sortVisitasByOrden(full.visitas || []);
    setPlanDetail(full);
    setPlanDetailVisitasDraft(visitasOrdenadas);
    setPlanOrderDirty(false);
    const operaciones = await loadMontosPorVisitaEnPlan(
      full.fecha,
      full.nroVendedor,
      usuarioPorVendedor.get(full.nroVendedor),
      visitasOrdenadas.map((v) => ({
        idVisita: v.idVisita,
        nroCliente: v.nroCliente,
        orden: v.orden,
        fechaHoraInicio: v.fechaHoraInicio,
        fechaHoraFin: v.fechaHoraFin,
        fechaHoraOmitida: v.fechaHoraOmitida,
      })),
    );
    setPlanDetailOperaciones(operaciones);
  };

  const handleRemoveVisitaFromPlan = async (visita: VisitasDto) => {
    if (!planDetail || !isVisitaPendiente(visita)) return;
    if (!window.confirm('¿Quitar esta visita pendiente del plan?')) return;

    try {
      setPlanDetailMutating(true);
      await visitasService.deleteVisita(visita.idVisita);
      enqueueSnackbar('Visita eliminada del plan.', { variant: 'success' });
      await reloadPlanDetail(planDetail.idVisitaPlan);
      await Promise.all([fetchPlanes(), fetchVisitas()]);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo eliminar la visita.'), { variant: 'error' });
    } finally {
      setPlanDetailMutating(false);
    }
  };

  const openAgregarClientesPlan = async (idVisitaPlan: number) => {
    try {
      const visitas =
        planDetail?.idVisitaPlan === idVisitaPlan
          ? planDetailVisitasDraft
          : (await visitasPlanService.getPlan(idVisitaPlan)).visitas || [];
      setAgregarClientesExistentes(visitas);
      setAgregarClientesPlanId(idVisitaPlan);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo obtener el plan.'), { variant: 'error' });
    }
  };

  const handleAgregarClientesPlan = async (items: CreateVisitasDto[]) => {
    if (!agregarClientesPlanId) return;
    let agregadas = 0;
    let ultimoError: string | null = null;
    const idVisitaPlan = agregarClientesPlanId;
    try {
      setAgregarClientesSaving(true);
      for (const item of items) {
        try {
          await visitasPlanService.addVisitaToPlan(idVisitaPlan, item);
          agregadas += 1;
        } catch (error: unknown) {
          ultimoError = getApiErrorMessage(error, `No se pudo agregar el cliente ${item.nroCliente}.`);
        }
      }
    } finally {
      setAgregarClientesSaving(false);
    }

    const fallidas = items.length - agregadas;
    if (fallidas === 0) {
      enqueueSnackbar(
        agregadas === 1 ? '1 visita agregada al plan.' : `${agregadas} visitas agregadas al plan.`,
        { variant: 'success' },
      );
      setAgregarClientesPlanId(null);
      setAgregarClientesExistentes([]);
    } else {
      enqueueSnackbar(
        `Visitas agregadas: ${agregadas}. Con error: ${fallidas}.${ultimoError ? ` ${ultimoError}` : ''}`,
        { variant: agregadas > 0 ? 'warning' : 'error' },
      );
      void visitasPlanService
        .getPlan(idVisitaPlan)
        .then((full) => setAgregarClientesExistentes(full.visitas || []))
        .catch(() => undefined);
    }

    if (agregadas > 0) {
      if (planDetail?.idVisitaPlan === idVisitaPlan) {
        void reloadPlanDetail(idVisitaPlan).catch(() => undefined);
      }
      void refreshAll();
    }
  };

  const handleClearPendientesPlan = async () => {
    if (!planDetail) return;
    const pendientes = planDetailVisitasDraft.filter(isVisitaPendiente);
    if (pendientes.length === 0) {
      enqueueSnackbar('No hay visitas pendientes en el plan.', { variant: 'info' });
      return;
    }
    if (
      !window.confirm(
        `¿Eliminar las ${pendientes.length} visitas pendientes del plan? Las iniciadas, completadas u omitidas se conservan.`,
      )
    ) {
      return;
    }

    try {
      setPlanDetailMutating(true);
      const res = await visitasPlanService.deleteVisitasPendientes(planDetail.idVisitaPlan);
      enqueueSnackbar(`Se eliminaron ${res.eliminadas} visitas pendientes.`, { variant: 'success' });
      await reloadPlanDetail(planDetail.idVisitaPlan);
      await Promise.all([fetchPlanes(), fetchVisitas()]);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron limpiar las visitas pendientes.'), {
        variant: 'error',
      });
    } finally {
      setPlanDetailMutating(false);
    }
  };

  const handleAppendVisita = async (payload: CreateVisitasDto) => {
    if (!appendPlan) return;
    try {
      setAppendSaving(true);
      await visitasPlanService.addVisitaToPlan(appendPlan.idVisitaPlan, payload);
      enqueueSnackbar('Visita agregada al plan correctamente', { variant: 'success' });
      setAppendOpen(false);
      setAppendPlan(null);
      await refreshAll();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al agregar visita al plan', {
        variant: 'error',
      });
    } finally {
      setAppendSaving(false);
    }
  };

  const handleCreateVisitaRapida = async (payload: CreateVisitasRapidaDto) => {
    try {
      setVisitaRapidaSaving(true);
      await visitasService.createVisitaRapida(payload);
      enqueueSnackbar('Visita rápida creada correctamente', { variant: 'success' });
      setVisitaRapidaOpen(false);
      await refreshAll();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al crear visita rápida', {
        variant: 'error',
      });
    } finally {
      setVisitaRapidaSaving(false);
    }
  };

  const openEstadoDialog = (action: 'iniciar' | 'completar' | 'omitir', visita: VisitasDto) => {
    setEstadoSaving(false);
    setEstadoAction(action);
    setSelectedVisita(visita);
    setEstadoOpen(true);
  };

  const patchVisitaInList = (updated: VisitasDto) => {
    setVisitasData((prev) => ({
      ...prev,
      items: (prev.items || []).map((v) => {
        if (v.idVisita !== updated.idVisita) return v;
        return {
          ...v,
          estado: updated.estado || v.estado,
          fechaHoraInicio: updated.fechaHoraInicio ?? v.fechaHoraInicio,
          fechaHoraFin: updated.fechaHoraFin ?? v.fechaHoraFin,
          fechaHoraOmitida: updated.fechaHoraOmitida ?? v.fechaHoraOmitida,
          motivoOmitida: updated.motivoOmitida ?? v.motivoOmitida,
          observaciones: updated.observaciones ?? v.observaciones,
          usuarioUltModificacion: updated.usuarioUltModificacion ?? v.usuarioUltModificacion,
          fechaUltModificacion: updated.fechaUltModificacion ?? v.fechaUltModificacion,
        };
      }),
    }));
  };

  const handleSubmitEstado = async (payload: UpdateVisitasEstadoDto) => {
    if (!selectedVisita) return;
    try {
      setEstadoSaving(true);
      let updated: VisitasDto;
      if (estadoAction === 'iniciar') {
        updated = await visitasService.iniciar(selectedVisita.idVisita, payload);
      } else if (estadoAction === 'completar') {
        updated = await visitasService.completar(selectedVisita.idVisita, payload);
      } else {
        updated = await visitasService.omitir(selectedVisita.idVisita, payload);
      }
      if (updated?.idVisita) {
        patchVisitaInList(updated);
      }
      enqueueSnackbar('Estado actualizado correctamente', { variant: 'success' });
      setEstadoOpen(false);
      setSelectedVisita(null);
      setEstadoSaving(false);
      // Refresco en segundo plano; no bloquear el diálogo ni las acciones.
      void fetchVisitas();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al actualizar estado de visita', {
        variant: 'error',
      });
      setEstadoSaving(false);
    }
  };

  const handleOpenHistorial = async (visita: VisitasDto) => {
    try {
      setHistorialVisitaId(visita.idVisita);
      setHistorialItems([]);
      setHistorialOpen(true);
      setHistorialLoading(true);
      const rows = await visitasService.getHistorial(visita.idVisita);
      setHistorialItems(rows);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cargar historial', {
        variant: 'error',
      });
      setHistorialOpen(false);
    } finally {
      setHistorialLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Visitas
            </Typography>
            <Stack direction="row" spacing={1}>
              {tab === 'planes' && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPlanCreateOpen(true)}>
                  Nuevo Plan
                </Button>
              )}
              {tab === 'visitas' && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setVisitaRapidaOpen(true)}
                >
                  Visita Rápida
                </Button>
              )}
            </Stack>
          </Stack>

          <Tabs value={tab} onChange={handleChangeTab} sx={{ mb: 2 }}>
            <Tab label="Planes" value="planes" />
            <Tab label="Visitas" value="visitas" />
            <Tab label="Agenda" value="agenda" />
          </Tabs>

          {tab === 'planes' && (
            <Box>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    value={planesFilters.fechaDesde}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, fechaDesde: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    value={planesFilters.fechaHasta}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, fechaHasta: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <VendedorSelect
                    value={planesFilters.nroVendedor ? Number(planesFilters.nroVendedor) : null}
                    onChange={(nro) =>
                      setPlanesFilters((p) => ({ ...p, nroVendedor: nro != null ? String(nro) : '' }))
                    }
                    vendedores={vendedores}
                    allowEmpty
                    emptyOptionLabel="Todos"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Estado"
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    value={planesFilters.estadoPlan}
                    onChange={(e) => setPlanesFilters((p) => ({ ...p, estadoPlan: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Abierto">Abierto</MenuItem>
                    <MenuItem value="EnCurso">EnCurso</MenuItem>
                    <MenuItem value="Cerrado">Cerrado</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setPlanesPage(0);
                        fetchPlanes();
                      }}
                    >
                      Buscar
                    </Button>
                    <Button
                      onClick={() => {
                        setPlanesFilters({
                          fechaDesde: '',
                          fechaHasta: '',
                          nroVendedor: '',
                          estadoPlan: 'EnCurso',
                        });
                        setPlanesPage(0);
                      }}
                    >
                      Limpiar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {isMobile ? (
                <Stack spacing={1.5}>
                  {planesData.items.map((plan) => (
                    <Card key={plan.idVisitaPlan} variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Plan #{plan.idVisitaPlan}
                        </Typography>
                        <Typography variant="body2">Fecha: {formatDate(plan.fecha)}</Typography>
                        <Typography variant="body2">
                          Vendedor: {formatVendedor(plan.nroVendedor, plan.nombreVendedor)}
                        </Typography>
                        <Typography variant="body2">
                          Estado:{' '}
                          <Chip label={plan.estadoPlan} size="small" color="primary" variant="outlined" />
                        </Typography>
                        <PlanIndicadoresMobileSummary plan={plan} />
                        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                          {renderPlanActions(plan)}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Vendedor</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Visitas</TableCell>
                        <TableCell align="right">Cobranzas</TableCell>
                        <TableCell align="right">Ventas</TableCell>
                        <TableCell align="right">Entregas</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planesLoading ? (
                        <TableRow>
                          <TableCell colSpan={9}>Cargando...</TableCell>
                        </TableRow>
                      ) : planesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9}>No hay datos para mostrar.</TableCell>
                        </TableRow>
                      ) : (
                        planesData.items.map((plan) => (
                          <TableRow key={plan.idVisitaPlan} hover>
                            <TableCell>{plan.idVisitaPlan}</TableCell>
                            <TableCell>{formatDate(plan.fecha)}</TableCell>
                            <TableCell>{formatVendedor(plan.nroVendedor, plan.nombreVendedor)}</TableCell>
                            <TableCell>
                              <Chip label={plan.estadoPlan} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <PlanIndicadoresTableCells plan={plan} />
                            <TableCell align="center">{renderPlanActions(plan)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={planesData.totalCount}
                page={planesPage}
                onPageChange={(_, newPage) => setPlanesPage(newPage)}
                rowsPerPage={planesRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setPlanesRowsPerPage(Number(e.target.value));
                  setPlanesPage(0);
                }}
                labelRowsPerPage="Filas por página:"
              />
            </Box>
          )}

          {tab === 'agenda' && <VisitasAgendaTab />}

          {tab === 'visitas' && (
            <Box>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="IdPlan"
                    value={visitasFilters.idVisitaPlan}
                    onChange={(e) => {
                      setVisitasFilters((p) => ({ ...p, idVisitaPlan: e.target.value }));
                      setVisitaFiltroPlanLabel('');
                    }}
                    helperText={
                      visitaFiltroPlanLabel
                        ? visitaFiltroPlanLabel
                        : visitasPlanOptionsLoading
                          ? 'Cargando planes...'
                          : 'Escriba N° o use el selector para buscar'
                    }
                    FormHelperTextProps={{
                      sx: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            color="primary"
                            title="Seleccionar plan"
                            onClick={() => setPlanFiltroSearchOpen(true)}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <VendedorSelect
                    value={visitasFilters.nroVendedor ? Number(visitasFilters.nroVendedor) : null}
                    onChange={(nro) =>
                      setVisitasFilters((p) => ({ ...p, nroVendedor: nro != null ? String(nro) : '' }))
                    }
                    vendedores={vendedores}
                    allowEmpty
                    emptyOptionLabel="Todos"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={visitasFilters.fechaDesde}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, fechaDesde: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={visitasFilters.fechaHasta}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, fechaHasta: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Estado"
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    value={visitasFilters.estado}
                    onChange={(e) => setVisitasFilters((p) => ({ ...p, estado: e.target.value }))}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Pendiente">Pendiente</MenuItem>
                    <MenuItem value="Iniciada">Iniciada</MenuItem>
                    <MenuItem value="Completada">Completada</MenuItem>
                    <MenuItem value="Omitida">Omitida</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Número de Cliente"
                    value={visitasFilters.nroCliente}
                    onChange={(e) => {
                      setVisitasFilters((p) => ({ ...p, nroCliente: e.target.value }));
                      setVisitaFiltroClienteLabel('');
                    }}
                    helperText={
                      visitaFiltroClienteLabel
                        ? visitaFiltroClienteLabel
                        : 'Escriba el número o use el selector para buscar'
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            color="primary"
                            title="Seleccionar cliente"
                            onClick={() => setClienteFiltroSearchOpen(true)}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setVisitasPage(0);
                        fetchVisitas();
                      }}
                    >
                      Buscar
                    </Button>
                    <Button
                      onClick={() => {
                        setVisitasFilters({
                          idVisitaPlan: '',
                          nroVendedor: '',
                          fechaDesde: '',
                          fechaHasta: '',
                          estado: '',
                          nroCliente: '',
                        });
                        setVisitaFiltroPlanLabel('');
                        setVisitaFiltroClienteLabel('');
                        setVisitasPage(0);
                      }}
                    >
                      Limpiar
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {isMobile ? (
                <Stack spacing={1.5}>
                  {visitasData.items.map((v) => (
                    <Card key={v.idVisita} variant="outlined">
                      <CardContent sx={{ py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Visita #{v.idVisita}
                        </Typography>
                        <Typography variant="body2">Plan: {v.idVisitaPlan}</Typography>
                        <Typography variant="body2">Cliente: {v.nroCliente}</Typography>
                        <Typography variant="body2">Orden: {v.orden}</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Estado: <Chip label={v.estado} size="small" color="primary" variant="outlined" />
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {isVisitaPendiente(v) && (
                            <IconButton size="small" color="primary" onClick={() => openEstadoDialog('iniciar', v)}>
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canCompletarVisita(v) && (
                            <IconButton size="small" color="success" onClick={() => openEstadoDialog('completar', v)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canOmitirVisita(v) && (
                            <IconButton size="small" color="warning" onClick={() => openEstadoDialog('omitir', v)}>
                              <SkipNextIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" color="info" onClick={() => handleOpenHistorial(v)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Orden</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Programada</TableCell>
                        <TableCell>Inicio</TableCell>
                        <TableCell>Fin</TableCell>
                        <TableCell>Omitida</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visitasLoading ? (
                        <TableRow>
                          <TableCell colSpan={10}>Cargando...</TableCell>
                        </TableRow>
                      ) : visitasData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10}>No hay datos para mostrar.</TableCell>
                        </TableRow>
                      ) : (
                        visitasData.items.map((v) => (
                          <TableRow key={v.idVisita} hover>
                            <TableCell>{v.idVisita}</TableCell>
                            <TableCell>{v.idVisitaPlan}</TableCell>
                            <TableCell>{v.orden}</TableCell>
                            <TableCell>{v.nroCliente}</TableCell>
                            <TableCell>
                              <Chip label={v.estado} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraProgramada)}</TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraInicio)}</TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraFin)}</TableCell>
                            <TableCell>{formatDateTime(v.fechaHoraOmitida)}</TableCell>
                            <TableCell align="center">
                              {isVisitaPendiente(v) && (
                                <Tooltip title="Iniciar visita">
                                  <IconButton size="small" color="primary" onClick={() => openEstadoDialog('iniciar', v)}>
                                    <PlayArrowIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canCompletarVisita(v) && (
                                <Tooltip title="Completar visita">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => openEstadoDialog('completar', v)}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canOmitirVisita(v) && (
                                <Tooltip title="Omitir visita">
                                  <IconButton size="small" color="warning" onClick={() => openEstadoDialog('omitir', v)}>
                                    <SkipNextIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Ver historial">
                                <IconButton size="small" color="info" onClick={() => handleOpenHistorial(v)}>
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={visitasData.totalCount}
                page={visitasPage}
                onPageChange={(_, newPage) => setVisitasPage(newPage)}
                rowsPerPage={visitasRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setVisitasRowsPerPage(Number(e.target.value));
                  setVisitasPage(0);
                }}
                labelRowsPerPage="Filas por página:"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <PlanFormDialog
        open={planCreateOpen}
        mode="create"
        vendedores={vendedores}
        saving={planSaving}
        onClose={() => setPlanCreateOpen(false)}
        onSubmitCreate={handleCreatePlan}
        onSubmitEdit={handleUpdatePlan}
      />

      <PlanFormDialog
        open={planEditOpen}
        mode="edit"
        vendedores={vendedores}
        plan={selectedPlan}
        saving={planSaving}
        onClose={() => {
          setPlanEditOpen(false);
          setSelectedPlan(null);
        }}
        onSubmitCreate={handleCreatePlan}
        onSubmitEdit={handleUpdatePlan}
      />

      <AppendVisitaDialog
        open={appendOpen}
        planFecha={appendPlan?.fecha}
        saving={appendSaving}
        onClose={() => {
          setAppendOpen(false);
          setAppendPlan(null);
        }}
        onSubmit={handleAppendVisita}
      />

      <AgregarClientesPlanDialog
        open={agregarClientesPlanId != null}
        visitasExistentes={agregarClientesExistentes}
        saving={agregarClientesSaving}
        onClose={() => {
          setAgregarClientesPlanId(null);
          setAgregarClientesExistentes([]);
        }}
        onSubmit={handleAgregarClientesPlan}
      />

      <VisitaRapidaDialog
        open={visitaRapidaOpen}
        vendedores={vendedores}
        saving={visitaRapidaSaving}
        onClose={() => setVisitaRapidaOpen(false)}
        onSubmit={handleCreateVisitaRapida}
      />

      <VisitaEstadoDialog
        open={estadoOpen}
        action={estadoAction}
        visita={selectedVisita}
        saving={estadoSaving}
        onClose={() => {
          if (estadoSaving) return;
          setEstadoOpen(false);
          setSelectedVisita(null);
          setEstadoSaving(false);
        }}
        onSubmit={handleSubmitEstado}
      />

      <VisitaHistorialDialog
        open={historialOpen}
        loading={historialLoading}
        visitaId={historialVisitaId}
        items={historialItems}
        onClose={() => {
          setHistorialOpen(false);
          setHistorialItems([]);
          setHistorialVisitaId(null);
        }}
      />

      <Dialog
        open={planDetailOpen}
        onClose={() => {
          setPlanDetailOpen(false);
          setPlanDetail(null);
          setPlanDetailVisitasDraft([]);
          setPlanDetailOperaciones({});
          setPlanOrderDirty(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Detalle del plan #{planDetail?.idVisitaPlan}</DialogTitle>
        <DialogContent dividers>
          {planDetailLoading ? (
            <Typography>Cargando...</Typography>
          ) : !planDetail ? (
            <Typography>No se pudo cargar el detalle.</Typography>
          ) : (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Fecha:</strong> {formatDate(planDetail.fecha)}
                </Typography>
                <Typography variant="body2">
                  <strong>Vendedor:</strong> {resolveVendedorUsuario(planDetail.nroVendedor, vendedores)}
                </Typography>
                <Typography variant="body2">
                  <strong>Estado:</strong> {planDetail.estadoPlan}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Observaciones:</strong> {planDetail.observaciones || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Solo las visitas pendientes se pueden arrastrar. Las demás mantienen su orden fijo.
              </Typography>
              <DndContext sensors={planDetailSensors} collisionDetection={closestCenter} onDragEnd={handlePlanDetailDragEnd}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Orden</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Dirección</TableCell>
                      <TableCell>Observaciones</TableCell>
                      <TableCell align="right">Cobranza</TableCell>
                      <TableCell align="right">Pedido</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Programada</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {planDetailVisitasDraft.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9}>No hay visitas asociadas.</TableCell>
                      </TableRow>
                    ) : (
                      <SortableContext
                        items={planDetailVisitasDraft.map((v) => String(v.idVisita))}
                        strategy={verticalListSortingStrategy}
                      >
                        {planDetailVisitasDraft.map((v) => (
                          <SortablePlanVisitaRow
                            key={v.idVisita}
                            visita={v}
                            operaciones={planDetailOperaciones[v.idVisita]}
                            removeDisabled={
                              planDetailMutating ||
                              planOrderSaving ||
                              String(planDetail.estadoPlan || '').toLowerCase() === 'cerrado'
                            }
                            onRemovePendiente={handleRemoveVisitaFromPlan}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </TableBody>
                </Table>
              </DndContext>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<GroupAddIcon />}
            disabled={
              !planDetail ||
              planDetailMutating ||
              planOrderSaving ||
              planDetailLoading ||
              String(planDetail?.estadoPlan || '').toLowerCase() === 'cerrado'
            }
            onClick={() => {
              if (planDetail) void openAgregarClientesPlan(planDetail.idVisitaPlan);
            }}
          >
            Agregar clientes
          </Button>
          <Button
            variant="outlined"
            color="warning"
            disabled={
              planDetailMutating ||
              planOrderSaving ||
              planDetailLoading ||
              String(planDetail?.estadoPlan || '').toLowerCase() === 'cerrado' ||
              !planDetailVisitasDraft.some(isVisitaPendiente)
            }
            onClick={() => void handleClearPendientesPlan()}
          >
            Limpiar pendientes
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePlanOrder}
            disabled={!planOrderDirty || planOrderSaving || planDetailMutating}
          >
            Guardar orden
          </Button>
          <Button
            onClick={() => {
              setPlanDetailOpen(false);
              setPlanDetail(null);
              setPlanDetailVisitasDraft([]);
              setPlanDetailOperaciones({});
              setPlanOrderDirty(false);
            }}
            disabled={planOrderSaving || planDetailMutating}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteSearch
        open={clienteFiltroSearchOpen}
        onClose={() => setClienteFiltroSearchOpen(false)}
        onSelect={(cliente) => {
          setVisitasFilters((p) => ({ ...p, nroCliente: String(cliente.nroCliente) }));
          setVisitaFiltroClienteLabel(
            [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') || `Cliente ${cliente.nroCliente}`,
          );
          setClienteFiltroSearchOpen(false);
        }}
      />

      <VisitasPlanSearchDialog
        open={planFiltroSearchOpen}
        loading={visitasPlanOptionsLoading}
        plans={visitasPlanOptions}
        onClose={() => setPlanFiltroSearchOpen(false)}
        onSelect={(plan) => {
          setVisitasFilters((p) => ({ ...p, idVisitaPlan: String(plan.idVisitaPlan) }));
          setVisitaFiltroPlanLabel(
            `#${plan.idVisitaPlan} - ${formatDate(plan.fecha)} - ${formatVendedor(plan.nroVendedor, plan.nombreVendedor)}`,
          );
          setPlanFiltroSearchOpen(false);
        }}
      />

      <Menu
        anchorEl={planActionsAnchor?.el}
        open={Boolean(planActionsAnchor)}
        onClose={handlePlanActionsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {planActionsAnchor ? (
          <>
            <MenuItem
              onClick={() => {
                handlePlanActionsClose();
                void handleDeletePlan(planActionsAnchor.plan);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Eliminar plan</ListItemText>
            </MenuItem>
            <MenuItem
              disabled={planPdfLoadingId === planActionsAnchor.plan.idVisitaPlan}
              onClick={() => {
                handlePlanActionsClose();
                void handleImprimirPlanPdf(planActionsAnchor.plan);
              }}
            >
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Imprimir / PDF</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handlePlanActionsClose();
                void openAgregarClientesPlan(planActionsAnchor.plan.idVisitaPlan);
              }}
            >
              <ListItemIcon>
                <GroupAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Agregar varios clientes</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handlePlanActionsClose();
                setSelectedPlan(toPlanDto(planActionsAnchor.plan));
                setPlanEditOpen(true);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Editar plan</ListItemText>
            </MenuItem>
          </>
        ) : null}
      </Menu>
    </Box>
  );
};

export default Visitas;
